export const DISPLAY_NAME_MAX_LENGTH = 25;
export const BIO_MAX_LENGTH = 128;

export const SPOTIFY_LINK_PLACEHOLDER = "https://open.spotify.com/track/…";

// The API (`extractSpotifyTrackId` in spotify.service.ts) currently only
// resolves Spotify *track* links/URIs, so we validate for those here to keep
// autosave from rejecting the draft. See SOM-4793 note about broadening this to
// playlists/artists to match the design copy.
const SPOTIFY_TRACK_REGEX =
  /(?:open\.spotify\.com\/(?:intl-[a-z-]+\/)?track\/|spotify:track:)([a-zA-Z0-9]+)/i;

export function extractSpotifyTrackId(value: string): string | null {
  return value.trim().match(SPOTIFY_TRACK_REGEX)?.[1] ?? null;
}

export function isValidSpotifyLink(value: string): boolean {
  return extractSpotifyTrackId(value) !== null;
}

export const DEFAULT_MENTOR_LANGUAGE = "English";

export const MENTOR_LANGUAGE_OPTIONS = [
  "English",
  "Spanish",
  "Mandarin",
  "French",
  "Arabic",
  "Tagalog",
  "Vietnamese",
  "Korean",
  "Portuguese",
  "ASL",
] as const;

export const BIO_EXAMPLES = [
  {
    text: "I'm a psychology student who wants to become a counselor. I became a mentor because I know how hard it can be to go through things alone, and I'm here so you don't have to.",
  },
  {
    text: "I'm a queer, neurodivergent, activist, and student who loves photography, fashion, food, and community events. I'm excited to be a mentor to help young people thrive!",
  },
  {
    text: "I'm a mental health advocate and peer support specialist who loves songwriting, iced coffee, and going to the gym. I became a mentor because I really care about helping people through tough moments and feel honored to be part of someone's journey.",
  },
] as const;

export const VIBE_SLOT_COUNT = 5;
export const VIBE_MIN_PHOTOS = 3;

// Guidance-only captions for the vibe board. Not shown on the public profile.
export const VIBE_PROMPT_BANK = [
  "a meme that's a little too accurate",
  "the last thing you saved because it was funny",
  "your wallpaper energy",
  "a color palette that feels like you",
  'the most "your room" photo on your phone',
  "a sky you stopped to take a photo of",
  "a meme that lives in your head rent-free",
  "something that makes you feel like a kid again",
  "a place you feel most like yourself",
  "an object in your home that's important to you",
  "your comfort rewatch (tv/movie)",
  "a snack situation you stand by",
  "the cover of your favorite book",
  "a fictional character you strongly identify with",
  "a cool place you'd like to visit",
  "a photo nobody else would think to take",
  "a photo you like for no reason",
  "the outfit that always gets a compliment",
  "your go-to weekend energy",
  "something that always makes you laugh",
] as const;

export const INTEREST_MIN_SELECTIONS = 3;
export const INTEREST_MAX_SELECTIONS = 4;
export const INTEREST_MIN_PHOTOS = 3;

// Figma Step 8 chip labels (MO-4 Mentor Onboarding).
export const MENTOR_INTEREST_OPTIONS = [
  "Music",
  "Gaming",
  "Art & drawing",
  "Sports",
  "Reading",
  "Movies & TV",
  "Cooking",
  "Photography",
  "Fashion",
  "Working out",
  "Anime",
  "Hiking & outdoors",
  "Coding",
  "Dance",
  "Skateboarding",
  "Volunteering",
] as const;

export type MentorInterestOption = (typeof MENTOR_INTEREST_OPTIONS)[number];

export const INTEREST_PHOTO_TIPS = [
  "Show yourself in the photos, teens love to see you doing the things you&apos;re interested in"
] as const;
